import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Label } from '../ui/label'
import { useHabitability } from '../../hooks/useQueries'

interface RemediatesSelectProps {
  org: string
  buildingAssetUid?: string | null
  value: string
  onChange: (value: string) => void
  id: string
  testId: string
}

/**
 * Lets a work package name the habitability breach it remediates. The options
 * come from the asset's habitability verdict (NON_COMPLIANT or UNKNOWN causes),
 * never from a hard-coded list: the label is the rule's own.
 */
export function RemediatesSelect({
  org,
  buildingAssetUid,
  value,
  onChange,
  id,
  testId,
}: RemediatesSelectProps) {
  const { t, i18n } = useTranslation('capex')
  const { data } = useHabitability(org, buildingAssetUid ?? '')

  const options = useMemo(() => {
    const lang = (i18n.language || 'en').slice(0, 2)
    const out: { uid: string; text: string }[] = []
    for (const lot of data?.lots ?? []) {
      for (const cause of [...(lot.failing ?? []), ...(lot.unverified ?? [])]) {
        if (!cause.evaluation_uid) continue
        const label = cause.label?.[lang] ?? cause.label?.en ?? cause.rule_code ?? ''
        out.push({ uid: cause.evaluation_uid, text: `${lot.asset_name ?? ''} · ${label}` })
      }
    }
    return out
  }, [data, i18n.language])

  if (!buildingAssetUid || options.length === 0) return null

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{t('remediates.label')}</Label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
        data-testid={testId}
      >
        <option value="">{t('remediates.none')}</option>
        {options.map((o) => (
          <option key={o.uid} value={o.uid}>
            {o.text}
          </option>
        ))}
      </select>
    </div>
  )
}
